import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  ArrowDownUp, Download, Upload, ChevronUp, ChevronDown, FileSpreadsheet, FileText,
  Save, Trash2, CheckCircle2, AlertTriangle, Loader2, Filter,
} from "lucide-react";

const SORGENTI = [
  { key: "noleggi", label: "Noleggi / Prenotazioni" },
  { key: "clienti", label: "Anagrafica Clienti" },
  { key: "incassi", label: "Movimenti incassi" },
];
const DATE_FMT = [
  { v: "%d/%m/%Y", l: "GG/MM/AAAA" },
  { v: "%Y-%m-%d", l: "AAAA-MM-GG" },
  { v: "%d/%m/%Y %H:%M", l: "GG/MM/AAAA HH:MM" },
  { v: "%d-%m-%Y", l: "GG-MM-AAAA" },
];
const SEP = [{ v: ",", l: "Virgola ," }, { v: ";", l: "Punto e virgola ;" }, { v: "tab", l: "Tabulazione" }];
const STATI = ["all", "richiesta", "confermata", "in_corso", "concluso", "annullata"];

export default function ImportExport() {
  const { hasPerm } = useAuth();
  return (
    <div className="p-5 sm:p-8 fade-in">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Dati & integrazioni</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight flex items-center gap-3"><ArrowDownUp className="h-9 w-9 text-[#f98125]" /> IMPORT / EXPORT</h1>
      </div>
      <Tabs defaultValue={hasPerm("import_export.export") ? "export" : "import"}>
        <TabsList className="bg-[#141414] border border-zinc-800 rounded-sm mb-6">
          {hasPerm("import_export.export") && <TabsTrigger value="export" data-testid="tab-export" className="data-[state=active]:bg-[#f98125] data-[state=active]:text-white rounded-sm"><Download className="h-4 w-4 mr-1.5" /> Esporta</TabsTrigger>}
          {hasPerm("import_export.import") && <TabsTrigger value="import" data-testid="tab-import" className="data-[state=active]:bg-[#f98125] data-[state=active]:text-white rounded-sm"><Upload className="h-4 w-4 mr-1.5" /> Importa clienti</TabsTrigger>}
        </TabsList>
        {hasPerm("import_export.export") && <TabsContent value="export"><ExportPanel /></TabsContent>}
        {hasPerm("import_export.import") && <TabsContent value="import"><ImportPanel /></TabsContent>}
      </Tabs>
    </div>
  );
}

function ExportPanel() {
  const [fields, setFields] = useState({});
  const [sorgente, setSorgente] = useState("noleggi");
  const [cols, setCols] = useState([]);
  const [separatore, setSeparatore] = useState(";");
  const [formatoData, setFormatoData] = useState("%d/%m/%Y");
  const [formato, setFormato] = useState("csv");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [stato, setStato] = useState("all");
  const [profiles, setProfiles] = useState([]);
  const [profileName, setProfileName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/export/fields").then((r) => setFields(r.data)).catch(() => {});
    loadProfiles();
  }, []);
  const loadProfiles = () => api.get("/export/profiles").then((r) => setProfiles(r.data)).catch(() => {});

  useEffect(() => {
    if (fields[sorgente]) setCols(fields[sorgente].map((f) => ({ field: f.field, header: f.label, enabled: true })));
  }, [sorgente, fields]);

  const move = (i, dir) => setCols((c) => {
    const j = i + dir; if (j < 0 || j >= c.length) return c;
    const n = [...c]; [n[i], n[j]] = [n[j], n[i]]; return n;
  });
  const toggle = (i) => setCols((c) => c.map((x, k) => k === i ? { ...x, enabled: !x.enabled } : x));
  const setHeader = (i, v) => setCols((c) => c.map((x, k) => k === i ? { ...x, header: v } : x));

  const buildBody = () => ({
    sorgente, formato, separatore, formato_data: formatoData,
    colonne: cols.filter((c) => c.enabled).map((c) => ({ field: c.field, header: c.header })),
    filtri: { ...(dateFrom ? { date_from: new Date(`${dateFrom}T00:00:00`).toISOString() } : {}), ...(dateTo ? { date_to: new Date(`${dateTo}T23:59:59`).toISOString() } : {}), ...(sorgente === "noleggi" ? { stato } : {}) },
  });

  const download = async () => {
    const enabled = cols.filter((c) => c.enabled);
    if (enabled.length === 0) { toast.error("Seleziona almeno una colonna"); return; }
    setBusy(true);
    try {
      const res = await api.post("/export/run", buildBody(), { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename=([^;]+)/);
      const fname = m ? m[1].trim() : `${sorgente}.${formato}`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = fname; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Esportazione scaricata");
    } catch (e) { toast.error("Errore durante l'esportazione"); }
    finally { setBusy(false); }
  };

  const saveProfile = async () => {
    if (!profileName.trim()) { toast.error("Inserisci un nome profilo"); return; }
    try {
      await api.post("/export/profiles", {
        nome: profileName, sorgente, separatore, formato_data: formatoData, formato,
        colonne: cols.filter((c) => c.enabled).map((c) => ({ field: c.field, header: c.header })),
      });
      toast.success("Profilo salvato"); setProfileName(""); loadProfiles();
    } catch (e) { toast.error("Errore"); }
  };
  const applyProfile = (p) => {
    setSorgente(p.sorgente); setSeparatore(p.separatore); setFormatoData(p.formato_data); setFormato(p.formato || "csv");
    const base = fields[p.sorgente] || [];
    const chosen = p.colonne || [];
    const chosenFields = chosen.map((c) => c.field);
    const ordered = [
      ...chosen.map((c) => ({ field: c.field, header: c.header, enabled: true })),
      ...base.filter((f) => !chosenFields.includes(f.field)).map((f) => ({ field: f.field, header: f.label, enabled: false })),
    ];
    setCols(ordered);
    toast.success(`Profilo "${p.nome}" caricato`);
  };
  const delProfile = async (id) => { try { await api.delete(`/export/profiles/${id}`); loadProfiles(); toast.success("Profilo eliminato"); } catch { toast.error("Errore"); } };

  const profilesForSource = useMemo(() => profiles.filter((p) => p.sorgente === sorgente), [profiles, sorgente]);

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-5">
        <div className="bg-[#141414] border border-zinc-800 rounded-sm p-5 space-y-4">
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Sorgente dati</Label>
              <Select value={sorgente} onValueChange={setSorgente}>
                <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" data-testid="export-sorgente"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-zinc-800">{SORGENTI.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Dal</Label><Input type="date" data-testid="export-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Al</Label><Input type="date" data-testid="export-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" /></div>
          </div>
          {sorgente === "noleggi" && (
            <div className="max-w-[220px]">
              <Label className="text-xs uppercase tracking-wider text-zinc-500 flex items-center gap-1"><Filter className="h-3 w-3" /> Stato</Label>
              <Select value={stato} onValueChange={setStato}>
                <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" data-testid="export-stato"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-zinc-800">{STATI.map((s) => <SelectItem key={s} value={s}>{s === "all" ? "Tutti" : s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
        </div>

        <div className="bg-[#141414] border border-zinc-800 rounded-sm p-5">
          <div className="text-xs uppercase tracking-wider text-zinc-500 mb-3">Colonne (spunta, ordina, rinomina intestazione)</div>
          <div className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1">
            {cols.map((c, i) => (
              <div key={c.field} data-testid={`export-col-${c.field}`} className={`flex items-center gap-2 rounded-sm border px-2 py-1.5 ${c.enabled ? "border-zinc-700 bg-[#0A0A0A]" : "border-zinc-800 opacity-50"}`}>
                <input type="checkbox" checked={c.enabled} onChange={() => toggle(i)} data-testid={`export-col-check-${c.field}`} className="accent-[#f98125]" />
                <span className="text-xs text-zinc-500 w-32 truncate shrink-0">{c.field}</span>
                <Input value={c.header} onChange={(e) => setHeader(i, e.target.value)} disabled={!c.enabled} className="h-8 bg-[#141414] border-zinc-800 rounded-sm text-sm" />
                <button onClick={() => move(i, -1)} className="p-1 text-zinc-600 hover:text-white"><ChevronUp className="h-4 w-4" /></button>
                <button onClick={() => move(i, 1)} className="p-1 text-zinc-600 hover:text-white"><ChevronDown className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="space-y-5">
        <div className="bg-[#141414] border border-zinc-800 rounded-sm p-5 space-y-4">
          <div className="text-xs uppercase tracking-wider text-zinc-500">Formato</div>
          <div className="flex gap-2">
            {[{ v: "csv", i: FileText, l: "CSV" }, { v: "xlsx", i: FileSpreadsheet, l: "XLSX" }].map((f) => (
              <button key={f.v} onClick={() => setFormato(f.v)} data-testid={`export-format-${f.v}`} className={`flex-1 flex items-center justify-center gap-2 rounded-sm border px-3 py-2.5 text-sm ${formato === f.v ? "border-[#f98125] bg-[#f98125]/10 text-white" : "border-zinc-800 text-zinc-400"}`}><f.i className="h-4 w-4" /> {f.l}</button>
            ))}
          </div>
          {formato === "csv" && (
            <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Separatore</Label>
              <Select value={separatore} onValueChange={setSeparatore}>
                <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" data-testid="export-sep"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-zinc-800">{SEP.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Formato data</Label>
            <Select value={formatoData} onValueChange={setFormatoData}>
              <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" data-testid="export-datefmt"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#141414] border-zinc-800">{DATE_FMT.map((d) => <SelectItem key={d.v} value={d.v}>{d.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <button onClick={download} disabled={busy} data-testid="export-download" className="w-full flex items-center justify-center gap-2 rounded-sm bg-[#f98125] text-white font-bold uppercase tracking-wide px-5 py-3 hover:bg-[#d96a12] transition-colors disabled:opacity-60">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Download className="h-5 w-5" />} Scarica
          </button>
        </div>

        <div className="bg-[#141414] border border-zinc-800 rounded-sm p-5 space-y-3">
          <div className="text-xs uppercase tracking-wider text-zinc-500">Profili di export</div>
          <div className="flex gap-2">
            <Input value={profileName} onChange={(e) => setProfileName(e.target.value)} placeholder="Nome profilo" data-testid="profile-name" className="h-9 bg-[#0A0A0A] border-zinc-800 rounded-sm text-sm" />
            <button onClick={saveProfile} data-testid="profile-save" className="flex items-center gap-1.5 rounded-sm border border-zinc-700 px-3 text-sm hover:bg-zinc-800"><Save className="h-4 w-4" /> Salva</button>
          </div>
          <div className="space-y-1.5">
            {profilesForSource.length === 0 && <div className="text-xs text-zinc-600">Nessun profilo per questa sorgente.</div>}
            {profilesForSource.map((p) => (
              <div key={p.id} data-testid={`profile-${p.id}`} className="flex items-center justify-between gap-2 rounded-sm bg-[#0A0A0A] border border-zinc-800 px-3 py-2">
                <button onClick={() => applyProfile(p)} className="text-sm text-left flex-1 hover:text-[#f98125]">{p.nome} <span className="text-zinc-600 text-xs">({(p.colonne || []).length} col)</span></button>
                <button onClick={() => delProfile(p.id)} data-testid={`profile-del-${p.id}`} className="p-1 text-zinc-600 hover:text-[#FF3B30]"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ImportPanel() {
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [strategy, setStrategy] = useState("skip");
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setReport(null); setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/import/customers/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data); setMapping(r.data.guess || {});
    } catch (err) { toast.error(err.response?.data?.detail || "File non valido"); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const commit = async () => {
    if (!mapping.email) { toast.error("Mappa la colonna Email"); return; }
    setBusy(true);
    try {
      const r = await api.post("/import/customers/commit", { session_id: preview.session_id, mapping, strategy });
      setReport(r.data); setPreview(null);
      toast.success(`Import completato: ${r.data.created} creati, ${r.data.updated} aggiornati`);
    } catch (err) { toast.error(err.response?.data?.detail || "Errore import"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl space-y-5">
      {!preview && !report && (
        <div className="bg-[#141414] border border-zinc-800 border-dashed rounded-sm p-10 text-center">
          <Upload className="h-10 w-10 text-[#f98125] mx-auto mb-3" />
          <p className="text-zinc-400 text-sm mb-4">Carica un file <b>CSV</b> o <b>XLSX</b> con l'anagrafica clienti.</p>
          <label className="inline-flex items-center gap-2 rounded-sm bg-[#f98125] text-white font-bold px-5 py-2.5 cursor-pointer hover:bg-[#d96a12]">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Scegli file
            <input type="file" accept=".csv,.xlsx" onChange={onFile} data-testid="import-file" className="hidden" />
          </label>
        </div>
      )}

      {preview && (
        <>
          <div className="bg-[#141414] border border-zinc-800 rounded-sm p-5">
            <div className="text-sm mb-3"><span className="text-[#f98125] font-bold">{preview.total}</span> righe rilevate · {preview.columns.length} colonne</div>
            <div className="overflow-x-auto border border-zinc-800 rounded-sm">
              <table className="w-full text-xs">
                <thead><tr className="bg-[#0A0A0A]">{preview.columns.map((c) => <th key={c} className="px-2 py-1.5 text-left text-zinc-400 whitespace-nowrap">{c}</th>)}</tr></thead>
                <tbody>{preview.sample.map((row, i) => <tr key={i} className="border-t border-zinc-800/60">{preview.columns.map((c) => <td key={c} className="px-2 py-1 text-zinc-300 whitespace-nowrap">{row[c]}</td>)}</tr>)}</tbody>
              </table>
            </div>
          </div>

          <div className="bg-[#141414] border border-zinc-800 rounded-sm p-5 space-y-3">
            <div className="text-xs uppercase tracking-wider text-zinc-500">Mappatura colonne file → campo cliente</div>
            <div className="grid sm:grid-cols-2 gap-3">
              {preview.target_fields.map((f) => (
                <div key={f.field} className="flex items-center gap-2">
                  <span className="text-sm w-40 shrink-0">{f.label}{f.required && <span className="text-[#FF3B30]">*</span>}</span>
                  <Select value={mapping[f.field] || "__none__"} onValueChange={(v) => setMapping((m) => ({ ...m, [f.field]: v === "__none__" ? undefined : v }))}>
                    <SelectTrigger className="bg-[#0A0A0A] border-zinc-800 rounded-sm h-9" data-testid={`map-${f.field}`}><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent className="bg-[#141414] border-zinc-800">
                      <SelectItem value="__none__">— Non mappare —</SelectItem>
                      {preview.columns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-[#141414] border border-zinc-800 rounded-sm p-5 flex flex-wrap items-center gap-4">
            <div className="text-xs uppercase tracking-wider text-zinc-500">Duplicati per email</div>
            <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="radio" checked={strategy === "skip"} onChange={() => setStrategy("skip")} data-testid="strategy-skip" className="accent-[#f98125]" /> Salta</label>
            <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="radio" checked={strategy === "update"} onChange={() => setStrategy("update")} data-testid="strategy-update" className="accent-[#f98125]" /> Aggiorna</label>
            <div className="ml-auto flex gap-2">
              <button onClick={() => setPreview(null)} className="rounded-sm border border-zinc-700 px-4 py-2 text-sm hover:bg-zinc-800">Annulla</button>
              <button onClick={commit} disabled={busy} data-testid="import-commit" className="flex items-center gap-2 rounded-sm bg-[#f98125] text-white font-bold px-5 py-2 text-sm hover:bg-[#d96a12] disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Importa</button>
            </div>
          </div>
        </>
      )}

      {report && (
        <div className="bg-[#141414] border border-zinc-800 rounded-sm p-6" data-testid="import-report">
          <h3 className="font-display text-2xl tracking-tight mb-4">Report import</h3>
          <div className="grid grid-cols-4 gap-3 mb-4">
            <Stat label="Creati" value={report.created} color="text-[#39FF14]" />
            <Stat label="Aggiornati" value={report.updated} color="text-[#007AFF]" />
            <Stat label="Saltati" value={report.skipped} color="text-zinc-400" />
            <Stat label="Errori" value={report.errors.length} color="text-[#FF3B30]" />
          </div>
          {report.errors.length > 0 && (
            <div className="border border-zinc-800 rounded-sm max-h-60 overflow-y-auto">
              {report.errors.map((er, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-1.5 text-xs border-b border-zinc-800/60 text-zinc-400"><AlertTriangle className="h-3.5 w-3.5 text-[#FF3B30]" /> Riga {er.riga}: {er.motivo}</div>
              ))}
            </div>
          )}
          <button onClick={() => setReport(null)} className="mt-4 rounded-sm bg-[#f98125] text-white font-bold px-5 py-2.5 text-sm hover:bg-[#d96a12]">Nuovo import</button>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-4 text-center">
      <div className={`font-display text-3xl ${color}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">{label}</div>
    </div>
  );
}
